import axios from "axios";

// Robust API base resolution.
// At runtime in a browser we ALWAYS prefer same-origin so the frontend
// calls /api on whatever domain it's served from (sugarsense.com in
// production, the preview domain in preview). This makes the bundle
// portable across environments and removes any dependency on the
// REACT_APP_BACKEND_URL value baked in at build time.
// The env var is kept as a fall-back for non-browser contexts (SSR, tests).
const RUNTIME_ORIGIN =
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : "";
const BACKEND_URL =
  RUNTIME_ORIGIN || process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL.replace(/\/$/, "")}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// CSRF double-submit: read csrf_token cookie + send as X-CSRF-Token header on state-changing requests
function getCookie(name) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const token = getCookie("csrf_token");
    if (token) {
      config.headers["X-CSRF-Token"] = token;
    }
  }
  return config;
});

// Global 402 limit handler — any plan-limit reached anywhere in the app
// dispatches a "ss:limit-reached" CustomEvent so a single top-level modal
// can surface it without each call-site having to wire its own handler.
const LIMIT_CODES = new Set([
  "advisor_capacity_reached",
  "report_limit_reached",
  "strategy_limit_reached",
  "draft_limit_reached",
  "checkin_limit_reached",
  "profile_limit_reached",
]);

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 402 && !err?.config?.quiet402) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && LIMIT_CODES.has(detail.code)) {
        try {
          window.dispatchEvent(new CustomEvent("ss:limit-reached", { detail }));
        } catch { /* SSR safe */ }
      }
    }
    return Promise.reject(err);
  },
);

// Helper: normalise FastAPI error payloads to strings for toast/UI
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.code === "maintenance" ? `Maintenance in progress — ${detail.message}` : detail.message;
  return String(detail);
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const d = err?.response?.data?.detail;
    if (err?.response?.status === 423 && d && typeof d === "object" && d.code === "maintenance") {
      try { window.dispatchEvent(new CustomEvent("ss:maintenance", { detail: d })); } catch { /* SSR */ }
    }
    return Promise.reject(err);
  }
);

export function getErrorMessage(err) {
  return formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Unknown error";
}
